import * as React from "react";
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Pagination,
  Grid,
  Autocomplete,
  TextField,
} from "@mui/material";
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import Tooltip from '@mui/material/Tooltip';
import { useGetEngagementLogsQuery } from "../Slices/roomNateApis";
import { useGlobalContext } from "../context/GlobalContext";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useGetAllUsersQuery } from "../Slices/roomNateApis";

/** RTK Query caches `originalArgs` in Redux — must be JSON-serializable (no Dayjs). */
function serializeDateForEngagementQuery(value) {
  if (value == null || value === "") return null;
  if (typeof value === "string") {
    return value.length >= 10 ? value.slice(0, 10) : value;
  }
  if (typeof value.format === "function") {
    return value.format("YYYY-MM-DD");
  }
  return null;
}

function groupLogsByDate(logs) {
  const sorted = [...logs].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
  return sorted.reduce((acc, log) => {
    const date = new Date(log.timestamp).toISOString().slice(0, 10);
    if (!acc[date]) acc[date] = [];
    acc[date].push(log);
    return acc;
  }, {});
}

function LogSection({ title, logs, emptyText, onPageChange, page }) {
  const pageSize = 30;
  const grouped = logs && logs.items && logs.items.length > 0 ? groupLogsByDate(logs.items) : {};
  const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  const timeOptions = { hour: '2-digit', minute: '2-digit', second: '2-digit' };

  return (
    <Box
      sx={{
        flex: 1,
        borderBottom: "1px solid #eee",
        display: "flex",
        flexDirection: 'column',
        px: 2,
        mt: 1,
        height: '100%',
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="body1">{title}</Typography>
      </Box>
      {logs && logs.items && logs.items.length > 0 ? (
        <Box sx={{ ml: 2, width: "100%" }}>
          {Object.entries(grouped).map(([date, logsForDate]) => (
            <Box key={date} mb={2}>
              <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 600 }}>
                {new Date(date).toLocaleDateString(undefined, dateOptions)}
              </Typography>
              {logsForDate.map((log, idx) => (
                <Tooltip title={log?.savedLayout?.name || ''} placement="top" arrow disableHoverListener={!log?.savedLayout?.name} key={idx}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                      gap: 2,
                      px: 1,
                      py: 1,
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      window.open(`${window.location.origin}/floor-gallery/${log?.savedLayout?._id}`, '_blank');
                    }}
                  >
                    <Typography variant="body2" color="textSecondary" sx={{ minWidth: 90 }}>
                      {new Date(log.timestamp).toLocaleTimeString(undefined, timeOptions)}
                    </Typography>
                    <Box sx={{ minWidth: 180, maxWidth: 260, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', cursor: log?.savedLayout?.name ? 'pointer' : 'default' }}>
                      <Typography variant="body2" fontWeight="bold" noWrap>
                        {log.user?.name || '-'}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              ))}
            </Box>
          ))}
        </Box>
      ) : (
        <Typography variant="body2" color="textSecondary" sx={{ ml: 2 }}>
          {emptyText}
        </Typography>
      )}
      {logs && logs.total / pageSize > 1 && (
        <Box display="flex" justifyContent="center" mt={2} mb={1}>
          <Pagination
            count={Math.ceil(logs.total / pageSize)}
            page={page}
            onChange={(_, page) => onPageChange(page)}
            color="primary"
            size="small"
          />
        </Box>
      )}
    </Box>
  );
}


export default function EngagementLogs({drawerOpen, onClose, startDate, endDate, layout, tab}) {
  const {activePartner } = useGlobalContext();
  const [tabValue, setTabValue] = React.useState(0);
  const [filter, setFilter] = React.useState({
    views: 1,
    downloads: 1,
    shares: 1,
    startDate,
    endDate,
    layoutId: layout?._id,
  });



  

  const engagementLogsQueryArg = React.useMemo(
    () => ({
      partnerId: activePartner,
      downloadsPage: filter.downloads,
      viewsPage: filter.views,
      sharesPage: filter.shares,
      startDate: serializeDateForEngagementQuery(filter.startDate),
      endDate: serializeDateForEngagementQuery(filter.endDate),
      layoutId: filter.layoutId,
      userId: filter.userId,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }),
    [
      activePartner,
      filter.downloads,
      filter.views,
      filter.shares,
      filter.startDate,
      filter.endDate,
      filter.layoutId,
      filter.userId,
    ]
  );

  const { data: engagementData, isFetching: engagementDataFetching } =
    useGetEngagementLogsQuery(engagementLogsQueryArg, {
      refetchOnMountOrArgChange: true,
    });

  const { data: users } = useGetAllUsersQuery(null, {
    refetchOnMountOrArgChange: true,
  });

  // Sync props into filter when drawer context changes. Do NOT list `filter` in deps —
  // that caused setFilter → re-render → effect → setFilter (maximum update depth exceeded).
  React.useEffect(() => {
    setFilter((prev) => ({
      ...prev,
      startDate,
      endDate,
      layoutId: layout?._id,
    }));
  }, [startDate, endDate, layout?._id]);

  React.useEffect(() => {
    if(tab!==undefined) {
      setTabValue(tab);
    }
  }, [tab]);

  return (
    <Drawer
      anchor="right"
      open={drawerOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 400,
          borderTopLeftRadius: 8,
          borderBottomLeftRadius: 8,
          height: '100vh',
          overflow: 'auto'
        }
      }}
    >
      <Box
        sx={{
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          py: 1,
          backgroundColor: "#f5f5f5"
        }}
      >
        <Typography variant="h6">Engagement Logs {layout?.name ? `(${layout?.name})` : ""}</Typography>
        <IconButton onClick={onClose}>
          <CloseOutlinedIcon />
        </IconButton>
      </Box>

      <Box sx={{ padding: "12px" }}>
          <Grid container spacing={2}>
          {(
              <>
                
            
                  <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer
                          components={["DatePicker"]}
                          sx={{ ".MuiFormControl-root": { width: "100%" } }}
                          >
                          <DatePicker
                              label="Start Date"
                              value={filter.startDate}
                              onChange={(value) => setFilter({ ...filter, startDate: value })}
                              slotProps={{
                              actionBar: {
                                  actions: ["clear"],
                              },
                              }}
                          />
                          </DemoContainer>
                      </LocalizationProvider>
                  </Grid>
                  

                  <Grid item xs={12}>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer
                              components={["DatePicker"]}
                              sx={{ ".MuiFormControl-root": { width: "100%" } }}
                              >
                              <DatePicker
                                  label="End Date"
                                  minDate={filter.startDate}
                                  value={filter.endDate}
                                  onChange={(value) => setFilter({ ...filter, endDate: value })}
                                  slotProps={{
                                  actionBar: {
                                      actions: ["clear"],
                                  },
                                  }}
                              />
                          </DemoContainer>
                      </LocalizationProvider>
                  </Grid>

                  <Grid item xs={12}>
                    <Autocomplete
                        disablePortal
                        id="combo-box-demo"
                        value={{label: users?.find((user) => user?._id === filter.userId)?.name || "", value: filter.userId}}
                        options={users?.map((user) => ({label: user?.name || "", value: user?._id})) || []}
                        onChange={(event, value) =>
                            setFilter({ ...filter, userId: value?.value })
                        }
                        renderInput={(params) => (
                            <TextField {...params} label="Select User" />
                        )}
                    />
                  </Grid>


              </>
          )}
          </Grid>
      </Box>


      <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100% - 320px)' }}>
        <Tabs
          value={tabValue}
          onChange={(_, v) => setTabValue(v)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label={`Views (${engagementData?.logs?.views?.total || 0})`} />
          <Tab label={`Downloads (${engagementData?.logs?.downloads?.total || 0})`} />
          <Tab label={`Shares (${engagementData?.logs?.shares?.total || 0})`} />
        </Tabs>
        {
          engagementDataFetching ? (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center", 
                    alignItems: "center",    
                    height: "200px",         
                    width: "100%",
                    padding: "20px"
                }}
            >
                <Box display="flex" alignItems="center" justifyContent="center">
                    <IconButton color="inherit">
                    <CircularProgress size={19} color="inherit" />
                    </IconButton>
                    <Typography ml={1} color="inherit">
                    Loading
                    </Typography>
                </Box>
            </Box>
          ) : (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
          {tabValue === 0 && (
            <LogSection
              title="Recent Views"
              logs={engagementData?.logs?.views}
              emptyText="0 views logged"
              page={filter.views}
              onPageChange={(page) => setFilter({ ...filter, views: page })}
            />
          )}
          {tabValue === 1 && (
            <LogSection
              title="Recent Downloads"
              logs={engagementData?.logs?.downloads}
              emptyText="0 downloads"
              page={filter.downloads}
              onPageChange={(page) => setFilter({ ...filter, downloads: page })}
            />
          )}
          {tabValue === 2 && (
            <LogSection
              title="Recent Shares"
              logs={engagementData?.logs?.shares}
              emptyText="0 shares logged"
              page={filter.shares}
              onPageChange={(page) => setFilter({ ...filter, shares: page })}
            />
          )}
        </Box>
          )
        }
      </Box>
    </Drawer>
  );
}